import React, { useRef, useState, useEffect, useCallback } from "react";
import { Breadcrumb, Form, Input, Button, Space, Table, Tooltip, message } from "antd";
import { CheckCircleOutlined, SearchOutlined, StopOutlined } from "@ant-design/icons";
import type { InputRef, TableColumnsType, TableColumnType } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import "./list_user.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const ListUser: React.FC = () => {
     const transformStyle = { transform: "scale(0.80)", transformOrigin: "top left", width: "125%" };
     const backendUrl = process.env.REACT_APP_BACKEND_URL;
     const navigate = useNavigate();
     const { socket } = useAuth();
     const [onlineUsers, setOnlineUsers] = useState<Record<string, 'active' | 'inactive'>>({});
     const [warehouses, setWarehouses] = useState<Record<string, string>>({});
     interface UserDataType {
          key: string;
          kode_grup1: string;
          nama: string;
          users: string;
          expired_date: string;
          tgl_login: string;
          tgl_logout: string;
          status: string;
          password?: string;
          jabatan?: string;
          warehouse_code?: string;
          otoritas?: string;
          menu_otoritas?: string;
          auth_token?: string
          last_activity_timestamp?: string;
     }

     type UserDataIndex = keyof UserDataType;
     const [userData, setUserData] = useState<UserDataType[]>([]);
     const [loading, setLoading] = useState(true);
     const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

     const fetchUsers = useCallback(async () => {
          if (!backendUrl) {
               console.error("REACT_APP_BACKEND_URL is not defined!");
               setLoading(false);
               return;
          }
          try {
               const response = await axios.get(`${backendUrl}/api/user-management/users`);

               const mappedData = response.data.map((item: any, index: number) => ({
                    ...item,
                    key: item.kode_grup1 || String(index),
               }));
               setUserData(mappedData.filter((user: UserDataType) => user.users !== 'edp'));
          } catch (error) {
               console.error("Error fetching user data:", error);
               message.error("Failed to fetch user data.");
          } finally {
               setLoading(false);
          }
     }, [backendUrl]);

     const fetchWarehouses = useCallback(async () => {
          if (!backendUrl) return;
          try {
               const response = await axios.get(`${backendUrl}/api/user-management/warehouses`);
               const warehouseMap = response.data.reduce((acc: Record<string, string>, item: any) => {
                    acc[item.warehouse_code] = item.warehouse_name;
                    return acc;
               }, {});
               setWarehouses(warehouseMap);
          } catch (error) {
               console.error("Error fetching warehouses:", error);
          }
     }, [backendUrl]);

     useEffect(() => {
          fetchUsers();
          fetchWarehouses();
     }, [fetchUsers, fetchWarehouses]);

     useEffect(() => {
          if (!socket) return;
          socket.on("active_users_list", (users: Record<string, 'active' | 'inactive'>) => {
               setOnlineUsers(users);
          });
          socket.on("user_status_change", ({ userId, status }: { userId: string, status: 'active' | 'inactive' | 'offline' }) => {
               setOnlineUsers(prev => {
                    const updated = { ...prev };
                    if (status === 'offline') {
                         delete updated[userId];
                    } else {
                         updated[userId] = status;
                    }
                    return updated;
               });
          });
          return () => {
               socket.off("active_users_list");
               socket.off("user_status_change");
          };
     }, [socket]);

     const handleUpdateStatus = async (kode_grup1: string, newStatus: '1' | '2') => {
          setUpdatingStatus(kode_grup1);
          try {
               if (!backendUrl) {
                    message.error("Backend URL is not defined!");
                    return;
               }
               await axios.put(`${backendUrl}/api/user-management/update-status`, { kode_grup1, status: newStatus });
               message.success(`Status for User ID ${kode_grup1} updated successfully!`);
               fetchUsers();
          } catch (error) {
               console.error("Error updating user status:", error);
               message.error("Failed to update user status.");
          } finally {
               setUpdatingStatus(null);
          }
     };


     const [searchText, setSearchText] = useState("");
     const [searchedColumn, setSearchedColumn] = useState<UserDataIndex | "">("");

     const searchInput = useRef<InputRef>(null);

     const handleSearch = (
          selectedKeys: string[],
          confirm: FilterDropdownProps["confirm"],
          dataIndex: UserDataIndex,
     ) => {
          confirm();
          setSearchText(selectedKeys[0]);
          setSearchedColumn(dataIndex);
     };

     const handleReset = (clearFilters: () => void) => {
          clearFilters();
          setSearchText("");
     };

     const getColumnSearchProps = (dataIndex: UserDataIndex): TableColumnType<UserDataType> => ({
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
               <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                         ref={searchInput}
                         placeholder={`Search ${String(dataIndex)
                              .replace(/([A-Z])/g, " $1")
                              .trim()}`}
                         value={selectedKeys[0]}
                         onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                         onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                         style={{ marginBottom: 8, display: "block" }}
                    />
                    <Space>
                         <Button
                              type="primary"
                              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                              icon={<SearchOutlined />}
                              size="small"
                              style={{ width: 90 }}>
                              Search
                         </Button>
                         <Button
                              onClick={() => clearFilters && handleReset(clearFilters)}
                              size="small"
                              style={{ width: 90 }}>
                              Reset
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   confirm({ closeDropdown: false });
                                   setSearchText((selectedKeys as string[])[0]);
                                   setSearchedColumn(dataIndex);
                              }}>
                              Filter
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   close();
                              }}>
                              close
                         </Button>
                    </Space>
               </div>
          ),
          filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
          onFilter: (value, record) =>
               String(record[dataIndex])
                    .toLowerCase()
                    .includes((value as string).toLowerCase()),
          filterDropdownProps: {
               onOpenChange(open) {
                    if (open) {
                         setTimeout(() => searchInput.current?.select(), 100);
                    }
               },
          },
          render: (text) =>
               searchedColumn === dataIndex ? (
                    <Highlighter
                         highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                         searchWords={[searchText]}
                         autoEscape
                         textToHighlight={text ? text.toString() : ""}
                    />
               ) : (
                    text
               ),
     });

     const userColumns: TableColumnsType<UserDataType> = [
          {
               title: "User ID",
               dataIndex: "kode_grup1",
               key: "kode_grup1",
               ...getColumnSearchProps("kode_grup1"),
               sorter: (a, b) => a.kode_grup1.localeCompare(b.kode_grup1),
               sortDirections: ["ascend", "descend"],
               ellipsis: true,
          },
          {
               title: "Name",
               dataIndex: "nama",
               key: "nama",
               ...getColumnSearchProps("nama"),
               sorter: (a, b) => a.nama.localeCompare(b.nama),
               sortDirections: ["ascend", "descend"],
               ellipsis: true,
          },
          {
               title: "Username",
               dataIndex: "users",
               key: "users",
               ...getColumnSearchProps("users"),
               sorter: (a, b) => a.users.localeCompare(b.users),
               sortDirections: ["ascend", "descend"],
               ellipsis: true,
          },
          {
               title: "Warehouse",
               dataIndex: "warehouse_code",
               key: "warehouse_code",
               filters: Object.entries(warehouses).map(([code, name]) => ({ text: name, value: code })), 
               onFilter: (value, record) => record.warehouse_code === value, 
               filterSearch: true, 
               sorter: (a, b) => {
                    const nameA = warehouses[a.warehouse_code || ""] || "";
                    const nameB = warehouses[b.warehouse_code || ""] || "";
                    return nameA.localeCompare(nameB);
               },
               sortDirections: ["ascend", "descend"],
               render: (code: string) => warehouses[code] || code || "-",
               ellipsis: true,
          },
          {
               title: "Expired Date",
               dataIndex: "expired_date",
               key: "expired_date",
               ...getColumnSearchProps("expired_date"),
               sorter: (a, b) => new Date(a.expired_date).getTime() - new Date(b.expired_date).getTime(),
               sortDirections: ["ascend", "descend"],
               ellipsis: true,
          },
          {
               title: "Date Login",
               dataIndex: "tgl_login",
               key: "tgl_login",
               ...getColumnSearchProps("tgl_login"),
               sorter: (a, b) => new Date(a.tgl_login).getTime() - new Date(b.tgl_login).getTime(),
               sortDirections: ["ascend", "descend"],
               ellipsis: true,
          },
          {
               title: "Date Logout",
               dataIndex: "tgl_logout",
               key: "tgl_logout",
               ...getColumnSearchProps("tgl_logout"),
               sorter: (a, b) => new Date(a.tgl_logout).getTime() - new Date(b.tgl_logout).getTime(),
               sortDirections: ["ascend", "descend"],
               render: (text: string, record) => {
                    const status = onlineUsers[record.kode_grup1];
                    if (status === 'active') return 'Active';
                    if (status === 'inactive') return 'Inactive';
                    return text || '-';
               },
               ellipsis: true,
          },
          {
               title: "Status",
               dataIndex: "status",
               key: "status",
               ...getColumnSearchProps("status"),
               sorter: (a, b) => a.status.localeCompare(b.status),
               sortDirections: ["ascend", "descend"],
               render: (text: string) => {
                    if (text === '1') {
                         return 'Active';
                    } else if (text === '2') {
                         return 'Deactive';
                    }
                    return text;
               },
          },
          {
               title: "Info",
               key: "info",
               render: (_, record) => {
                    const status = onlineUsers[record.kode_grup1];
                    const isOnline = record.status === '1' && status;
                    return (
                         <span style={{ color: isOnline ? (status === 'active' ? 'green' : 'orange') : 'red' }}>
                              {isOnline ? (status === 'active' ? 'Online (Active)' : 'Online (Inactive)') : 'Offline'}
                         </span>
                    );
               },
               width: "5%",
          },
          {
               title: "Action",
               key: "action",
               render: (_, record) => (
                    <Space size="middle">
                         <Tooltip title="Active"> { }
                              <Button
                                   icon={<CheckCircleOutlined />}
                                   onClick={() => handleUpdateStatus(record.kode_grup1, '1')}
                                   loading={updatingStatus === record.kode_grup1}
                                   type="primary"
                                   size="small"
                              />
                         </Tooltip> { }
                         <Tooltip title="Deactive"> { }
                              <Button
                                   icon={<StopOutlined />}
                                   onClick={() => handleUpdateStatus(record.kode_grup1, '2')}
                                   loading={updatingStatus === record.kode_grup1}
                                   type="link"
                                   size="small"
                              />
                         </Tooltip> { }
                    </Space>
               ),
               width: "10%",
          },
     ];

     return (
          <div id="iypr" style={transformStyle}>
               <div className="gjs-grid-row" id="it0zo">
                    <div className="gjs-grid-column" id="izsop">
                         <Breadcrumb
                              items={[
                                   {
                                        title: (
                                             <>
                                                  <span>List User</span>
                                             </>
                                        ),
                                   },
                                   { href: "#/dashboard/management_user", title: "Management User" },
                              ]}
                         />
                    </div>
               </div>

               <div className="gjs-grid-row" id="inucs">
                    <div className="gjs-grid-column" id="io99f">
                         <Table<UserDataType>
                              columns={userColumns}
                              dataSource={userData}
                              loading={loading}
                              onRow={(record) => {
                                   return {
                                        onDoubleClick: () => {
                                             sessionStorage.setItem('selectedUserData', JSON.stringify(record));
                                             navigate('/dashboard/management_user');
                                        },
                                   };
                              }}
                         />
                    </div>
               </div>
          </div>
     );
};

export default ListUser;
